import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { ProductService, ServiceService } from '@beautyapp/core';
import { Store } from '@ngxs/store';
import { takeUntil } from 'rxjs/operators';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzFormModule } from 'ng-zorro-antd/form';
import { InputComponent, SelectSearchComponent } from '@beautyapp/form';
import {
  TranslateModule,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { CommonModule } from '@angular/common';

type AmountUnit = 'gram' | 'piece';

@Component({
  selector: 'beautyapp-appointment-product-add-edit',
  templateUrl: './appointment-product-add-edit.component.html',
  styleUrls: ['./appointment-product-add-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzGridModule,
    NzFormModule,
    InputComponent,
    SelectSearchComponent,
    TranslateModule,
    TranslatePipe,
    NzButtonModule,
    NzModalModule,
    NzSegmentedModule,
  ],
})
export class AppointmentProductAddEditComponent implements OnInit, OnDestroy {
  modal = inject(NzModalRef);
  private service = inject(ServiceService);
  private productService = inject(ProductService);
  private fb = inject(UntypedFormBuilder);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);

  readonly data = inject(NZ_MODAL_DATA);

  get appointmentProduct() {
    return this.data.appointmentProduct;
  }

  get bookingId() {
    return this.data.bookingId;
  }

  get serviceId() {
    return this.data.serviceId;
  }

  form: UntypedFormGroup;

  sub$: Subject<any> = new Subject<any>();
  products: any;
  product: any = null;

  // How much of the product the visit uses, and the unit that number is read
  // in. These stay outside the form because `form.value` is handed straight
  // back to the products list as the row payload.
  amount: number | null = null;
  unit: AmountUnit = 'gram';

  readonly unitOptions = [
    { label: this.translate.instant('calendar.form.gram'), value: 'gram' },
    { label: this.translate.instant('calendar.form.piece'), value: 'piece' },
  ];

  constructor() {
    this.form = this.fb.group({
      product: null,
      productId: [null, Validators.required],
      serviceId: null,
      quantitySum: null,
      grSum: null,
      price: null,
      priceValue: null,
      extra: false,
    });
  }

  /** Products measured by weight carry a per-item gram weight; the rest are
   *  counted whole and have no gram representation at all. */
  get gramTracked(): boolean {
    return +this.product?.grPerItem > 0;
  }

  get amountValue(): number {
    return Number(this.amount ?? 0) || 0;
  }

  /** What the visit takes off the shelf, always expressed in grams. */
  get gramsUsed(): number {
    if (!this.gramTracked) return 0;
    return this.unit === 'piece'
      ? this.amountValue * +this.product.grPerItem
      : this.amountValue;
  }

  /** True once the amount asks for more than the branch actually holds. Stock
   *  is allowed to go negative on save, so this is a warning, not a block. */
  get exceedsStock(): boolean {
    if (!this.product) return false;
    return this.gramTracked
      ? this.gramsUsed > (+this.product.grSum || 0)
      : this.amountValue > (+this.product.quantity || 0);
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }

  ngOnInit(): void {
    this.form.patchValue({
      serviceId: this.serviceId,
    });

    // Preload so the product dropdown has options on the first open (add mode).
    // Without this the list was empty until the user typed a search.
    if (!this.appointmentProduct) {
      this.searchProduct('');
    }

    if (this.appointmentProduct) {
      // Guard against missing nested product — without these checks, when
      // an item arrives without its product relation populated the .name
      // access throws and ngOnInit crashes, leaving the modal stuck in
      // "add" mode with an empty form.
      const productName = this.appointmentProduct.product?.name;
      if (productName) {
        this.searchProduct(productName);
      }
      this.product = this.appointmentProduct.product ?? null;
      this.form.patchValue({
        product: this.appointmentProduct.product ?? null,
        productId: this.appointmentProduct.productId ?? null,
        quantitySum: this.appointmentProduct.quantitySum ?? null,
        grSum: this.appointmentProduct.grSum ?? null,
        price: this.appointmentProduct.price ?? null,
        priceValue: this.appointmentProduct.priceValue ?? null,
        extra: this.appointmentProduct.extra ?? false,
      });
      this.readAmountBack();

      // If the nested .product relation wasn't loaded by the parent list,
      // fetch it by productId so the amount field knows the product's unit
      // and gram weight (otherwise this.product stays null and the row reads
      // as untracked).
      if (!this.product && this.appointmentProduct.productId) {
        this.productService
          .getById(this.appointmentProduct.productId)
          .pipe(takeUntil(this.sub$))
          .subscribe((res) => {
            this.product = res;
            this.form.patchValue({ product: res }, { emitEvent: false });
            this.readAmountBack();
            this.cdr.markForCheck();
          });
      }
    }
  }

  submit(): void {
    for (const key in this.form.controls) {
      this.form.controls[key].markAsDirty();
      this.form.controls[key].updateValueAndValidity();
    }
    if (this.form.invalid) return;
    // Use 'update' whenever the modal was opened with an existing item
    // (appointmentProduct truthy). The previous check also required
    // appointmentProduct.product, which routed to 'create' — and therefore
    // appended a new row — when the nested product relation wasn't loaded.
    if (this.appointmentProduct) {
      this.modal.close({ product: this.form.value, action: 'update' });
    } else {
      this.modal.close({ product: this.form.value, action: 'create' });
    }
  }

  searchProduct(search: string) {
    this.productService
      .search(search)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.products = res;
        // OnPush: the async result must be flushed to the view, otherwise the
        // dropdown shows stale (empty) data until the next interaction — which
        // is why products only appeared on the second open.
        this.cdr.markForCheck();
      });
  }

  selected($event: any) {
    if (!$event) {
      this.product = null;
      this.amount = null;
      this.form.patchValue({
        product: null,
        quantitySum: null,
        grSum: null,
        price: 0,
        priceValue: 0,
      });
      return;
    }
    this.productService
      .getById($event)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.product = res;
        this.amount = null;
        this.unit = +res?.grPerItem > 0 ? 'gram' : 'piece';
        this.form.patchValue({
          product: res,
          quantitySum: null,
          grSum: null,
          price: 0,
          priceValue: 0,
        });
        this.cdr.markForCheck();
      });
  }

  /** A visit row answers one question — how much was used. Writing both a
   *  gram amount and a piece count let the two contradict each other, and
   *  stock then consumed the grams and silently dropped the piece count
   *  (beaut-444). Only one of the two columns is ever filled from here. */
  amountChanged(): void {
    const amount = this.amountValue;
    const unitPrice = +this.product?.salePricePerItem || 0;

    if (this.gramTracked) {
      const grams = this.gramsUsed;
      const value = (unitPrice / +this.product.grPerItem) * grams;
      this.form.patchValue({
        grSum: grams || null,
        quantitySum: null,
        price: this.round(value),
        priceValue: this.round(value),
      });
      return;
    }

    const value = unitPrice * amount;
    this.form.patchValue({
      grSum: null,
      quantitySum: amount || null,
      price: this.round(value),
      priceValue: this.round(value),
    });
  }

  /** Existing rows may carry both columns. The grams are what stock and cost
   *  actually honour, so they win when reading one back into the amount. */
  private readAmountBack(): void {
    const grams = +this.appointmentProduct?.grSum || 0;
    if (grams > 0) {
      this.amount = grams;
      this.unit = 'gram';
      return;
    }
    this.amount = +this.appointmentProduct?.quantitySum || null;
    this.unit = 'piece';
  }

  private round(value: number): number {
    return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
  }
}
